import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import { ReactElement, useEffect, useRef, useState } from "react";

import { Button } from "./catalyst/Button.tsx";
import { CopyButton } from "./CopyButton.tsx";

export interface UserMessageProps {
  message: string;
}

export const UserMessage = ({ message }: UserMessageProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLongMessage, setIsLongMessage] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };

  // Check if message is long (4+ lines) by measuring the height
  useEffect(() => {
    if (messageRef.current) {
      // Get the computed line height from the element's styles
      const computedStyle = window.getComputedStyle(messageRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight);

      // If lineHeight is 'normal', calculate it from font-size (typically 1.2x font-size)
      const fontSize = parseFloat(computedStyle.fontSize);
      const actualLineHeight = isNaN(lineHeight) ? fontSize * 1.2 : lineHeight;

      const maxHeight = actualLineHeight * 4; // 4 lines
      const actualHeight = messageRef.current.scrollHeight;
      setIsLongMessage(actualHeight > maxHeight);
    }
  }, [message]);

  return (
    <div className="flex flex-col items-end">
      <div className="max-w-[80%] rounded-lg bg-zinc-600 px-4 py-2 text-white">
        <div
          className={`whitespace-pre-wrap break-words text-sm transition-all duration-200 ${
            isLongMessage && !isExpanded ? "max-h-20 overflow-hidden" : ""
          }`}
          ref={messageRef}
        >
          {message}
        </div>
        {isLongMessage && (
          <div className="mt-2 flex justify-center">
            <Button
              className="text-gray-300 hover:text-white"
              onClick={toggleExpanded}
              plain
              size="xs"
              title={isExpanded ? "Collapse message" : "Expand message"}
            >
              {isExpanded ? (
                <>
                  <ChevronUpIcon className="h-3 w-3" data-slot="icon" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-3 w-3" data-slot="icon" />
                  Show more
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      <div className="mt-2 flex justify-end">
        <CopyButton
          content={message}
          errorMessage="Failed to copy message to clipboard"
          successMessage="Message copied to clipboard"
          title="Copy message"
        />
      </div>
    </div>
  );
};
